import api from './api';

export interface MarketingMultinivelConfig {
  id: number;
  ativo: boolean;
  tipo_desconto_cadastro: 'valor' | 'porcentagem';
  valor_desconto_cadastro: number;
  tipo_desconto_recorrente: 'valor' | 'porcentagem';
  niveis_ativos: number;
  createdAt: string;
  updatedAt: string;
}

export interface MarketingMultinivelNivel {
  id: number;
  nivel: number;
  valor_desconto: number;
  ativo: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface UpdateConfigData {
  ativo: boolean;
  tipo_desconto_cadastro: 'valor' | 'porcentagem';
  valor_desconto_cadastro: number;
  tipo_desconto_recorrente: 'valor' | 'porcentagem';
  niveis_ativos: number;
}

export interface UpdateNivelData {
  valor_desconto: number;
  ativo: boolean;
}

export interface CreateNivelData {
  nivel: number;
  valor_desconto: number;
  ativo?: boolean;
}

class MarketingMultinivelService {
  
  /**
   * Busca configurações gerais do módulo
   */
  async getConfig(): Promise<MarketingMultinivelConfig> {
    try {
      const data = await api.get('/marketing-multinivel/config') as MarketingMultinivelConfig;
      
      if (!data) {
        throw new Error('API retornou dados vazios');
      }
      
      return data;
    } catch (error: any) {
      console.error('❌ Erro na chamada getConfig:', error);
      if (error?.response) {
        console.error('📊 Status do erro:', error.response.status);
        console.error('📊 Data do erro:', error.response.data);
        console.error('📊 Headers do erro:', error.response.headers);
      } else if (error?.request) {
        console.error('📊 Request foi feito mas sem resposta:', error.request);
      } else {
        console.error('📊 Erro na configuração:', error?.message || error);
      }
      throw error;
    }
  }

  /**
   * Atualiza configurações gerais do módulo
   */
  async updateConfig(data: UpdateConfigData): Promise<MarketingMultinivelConfig> {
    const response = await api.put('/marketing-multinivel/config', data);
    return response as unknown as MarketingMultinivelConfig;
  }

  /**
   * Busca configurações de todos os níveis
   */
  async getNiveis(): Promise<MarketingMultinivelNivel[]> {
    const data = await api.get('/marketing-multinivel/niveis') as MarketingMultinivelNivel[];
    return data;
  }

  /**
   * Atualiza configurações de um nível específico
   */
  async updateNivel(id: number, data: UpdateNivelData): Promise<MarketingMultinivelNivel> {
    const response = await api.put(`/marketing-multinivel/niveis/${id}`, data);
    return response as unknown as MarketingMultinivelNivel;
  }

  /**
   * Cria um novo nível
   */
  async createNivel(data: CreateNivelData): Promise<MarketingMultinivelNivel> {
    const response = await api.post('/marketing-multinivel/niveis', data);
    return response as unknown as MarketingMultinivelNivel;
  }

  /**
   * Remove um nível
   */
  async deleteNivel(id: number): Promise<void> {
    await api.delete(`/marketing-multinivel/niveis/${id}`);
  }

  /**
   * Verifica se o módulo está ativo
   */
  async isModuleActive(): Promise<boolean> {
    try {
      const config = await this.getConfig();
      return config.ativo;
    } catch (error) {
      console.error('Erro ao verificar status do módulo:', error);
      return false;
    }
  }

  /**
   * Valida configurações antes de salvar
   */
  validateConfig(data: UpdateConfigData): string[] {
    const errors: string[] = [];

    if (data.valor_desconto_cadastro < 0) {
      errors.push('Valor de desconto de cadastro não pode ser negativo');
    }

    if (data.niveis_ativos < 1 || data.niveis_ativos > 5) {
      errors.push('Níveis ativos deve estar entre 1 e 5');
    }

    if (data.tipo_desconto_cadastro === 'porcentagem' && data.valor_desconto_cadastro > 100) {
      errors.push('Porcentagem de desconto de cadastro não pode ser maior que 100%');
    }

    return errors;
  }

  /**
   * Valida dados de nível antes de salvar
   */
  validateNivel(data: UpdateNivelData | CreateNivelData): string[] {
    const errors: string[] = [];

    if (data.valor_desconto < 0) {
      errors.push('Valor de desconto não pode ser negativo');
    }

    if ('nivel' in data) {
      if (data.nivel < 1 || data.nivel > 5) {
        errors.push('Nível deve estar entre 1 e 5');
      }
    }

    return errors;
  }

  /**
   * Formata valores monetários para exibição
   */
  formatCurrency(value: number): string {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  }

  /**
   * Formata porcentagem para exibição
   */
  formatPercentage(value: number): string {
    return `${value.toFixed(2)}%`;
  }

  /**
   * Calcula valor do desconto baseado no tipo
   */
  calculateDiscountValue(
    type: 'valor' | 'porcentagem',
    discountValue: number,
    baseValue: number
  ): number {
    if (type === 'valor') {
      return Math.min(discountValue, baseValue);
    } else {
      return (baseValue * discountValue) / 100;
    }
  }

  /**
   * Gera configuração padrão
   */
  getDefaultConfig(): UpdateConfigData {
    return {
      ativo: true,
      tipo_desconto_cadastro: 'valor',
      valor_desconto_cadastro: 15.00,
      tipo_desconto_recorrente: 'valor',
      niveis_ativos: 2
    };
  }

  /**
   * Gera configurações de níveis padrão
   */
  getDefaultNiveis(): CreateNivelData[] {
    return [
      { nivel: 1, valor_desconto: 3.00, ativo: true },
      { nivel: 2, valor_desconto: 2.00, ativo: true },
      { nivel: 3, valor_desconto: 1.00, ativo: false }
    ];
  }
}

export default new MarketingMultinivelService(); 